"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ClassicLoader from "@/components/common/classic-loader";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AuthInfoBox } from "@/features/auth/components/elements/auth-info-box";
import { NewPasswordField } from "@/features/auth/components/elements/new-password-field";
import { AUTH_PRIMARY_BUTTON } from "@/features/auth/lib/auth-button-styles";
import { AUTH_FORM_IDS } from "@/features/auth/lib/auth-step-config";
import { isPasswordValid } from "@/features/auth/lib/password-rules";

const FORM_ID = AUTH_FORM_IDS["reset-password"];

const formSchema = z.object({
  password: z.string().superRefine((value, ctx) => {
    if (value.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "auth.createPassword.validators.required",
      });
      return;
    }

    if (!isPasswordValid(value)) {
      ctx.addIssue({
        code: "custom",
        message: "auth.createPassword.validators.notMet",
      });
    }
  }),
});

type ResetPasswordStepProps = {
  email: string;
  isSubmitting: boolean;
  /** Translation key of a password error returned by Clerk. */
  serverError: string | null;
  /** Hides the inline submit so the host can render it in a bottom bar. */
  submitInFooter?: boolean;
  onSubmit: (password: string) => void;
};

/** Last step of the password reset: the new password for a verified account. */
export const ResetPasswordStep = ({
  email,
  isSubmitting,
  serverError,
  submitInFooter = false,
  onSubmit,
}: ResetPasswordStepProps) => {
  const t = useTranslations("auth");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "" },
  });

  const { setError } = form;

  useEffect(() => {
    if (serverError) {
      setError("password", { message: serverError });
    }
  }, [serverError, setError]);

  return (
    <div className="flex flex-col gap-6">
      <AuthInfoBox
        description={email}
        title={t("resetPassword.title")}
        truncateDescription
      />

      <Form {...form}>
        <form
          className="flex flex-col gap-6"
          id={FORM_ID}
          onSubmit={form.handleSubmit((values) => onSubmit(values.password))}
        >
          <NewPasswordField
            fieldName="password"
            form={form}
            label={t("resetPassword.label")}
          />

          {!submitInFooter && (
            <Button
              className={AUTH_PRIMARY_BUTTON}
              disabled={isSubmitting}
              type="submit"
            >
              {t("resetPassword.submit")}
              {isSubmitting && <ClassicLoader className="ml-2" />}
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
};
